import type { DatiMisureFotovoltaicoDTO } from '@/models/dto/fotovoltaico/dati-misure-fotovoltaico-dto'
import type { NuovoFotovoltaicoDTO } from '@/models/dto/fotovoltaico/fotovoltaico-dto'
import type { FotovoltaicoPaginationDTO } from '@/models/dto/fotovoltaico/fotovoltaico-pagination-dto'
import type { TipologieFotovoltaicoDTO } from '@/models/dto/fotovoltaico/tipologia-fotovoltaico-dto'
import { mapToFotovoltaicoDTO, mapToPhotovoltaicPlant } from '@/models/mappers/photovoltaic/photovoltaic-plant-mapper'
import { mapToPhotovoltaicPlants } from '@/models/mappers/photovoltaic/photovoltaic-plants-mapper'
import { mapToPhotovoltaicReadings } from '@/models/mappers/photovoltaic/photovoltaic-readings-mapper'
import { mapToPhotovoltaicModel } from '@/models/mappers/photovoltaic/photovoltaic-type-mapper'
import type { PhotovoltaicModel } from '@/models/types/photovoltaic/photovoltaic-model'
import type { NewPhotovoltaicPlant, PhotovoltaicPlant } from '@/models/types/photovoltaic/photovoltaic-plant'
import type { PhotovoltaicPlantsWithPagination } from '@/models/types/photovoltaic/photovoltaic-plants-with-pagination'
import type { PhotovoltaicReadings } from '@/models/types/photovoltaic/photovoltaic-readings'
import type { AggretationStrategy } from '@/models/types/plc/aggregation-strategy'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import { makeRequest, type MakeRequestOptions } from './make-request'

type PhotovoltaicParams = {
    order?: OrderType
    sort?: keyof NuovoFotovoltaicoDTO
    search?: string
    page?: string
} & Partial<Record<keyof NewPhotovoltaicPlant, string>>

type PhotovoltaicReadingsParams = {
    from?: string
    to?: string
    strategy?: AggretationStrategy
    onlyLastValue?: boolean
}

type PhotovoltaicCrudType = () => {
    getOne: (id: number, options?: MakeRequestOptions) => Promise<PhotovoltaicPlant>
    getOneByFacilityId: (id: number, options?: MakeRequestOptions) => Promise<PhotovoltaicPlant>
    getMany: (
        itemsPerPage: string,
        params?: PhotovoltaicParams,
        onlyEnabled?: boolean,
        options?: MakeRequestOptions,
    ) => Promise<PhotovoltaicPlantsWithPagination>
    patchFacility: (id: number, facilityId: number, options?: MakeRequestOptions) => Promise<PhotovoltaicPlant>
    getAll: (options?: MakeRequestOptions) => Promise<PhotovoltaicPlant[]>
    post: (photovoltaic: NewPhotovoltaicPlant, options?: MakeRequestOptions) => Promise<PhotovoltaicPlant>
    getAllModels: (options?: MakeRequestOptions) => Promise<PhotovoltaicModel[]>
    put: (photovoltaic: NewPhotovoltaicPlant, options?: MakeRequestOptions) => Promise<PhotovoltaicPlant>
    delete: (id: number, options?: MakeRequestOptions) => Promise<void>
    getProduction: (
        id: number,
        params?: PhotovoltaicReadingsParams,
        options?: MakeRequestOptions,
    ) => Promise<PhotovoltaicReadings>
    getConsumption: (
        id: number,
        params?: PhotovoltaicReadingsParams,
        options?: MakeRequestOptions,
    ) => Promise<PhotovoltaicReadings>
    getCo2: (
        id: number,
        params?: PhotovoltaicReadingsParams,
        options?: MakeRequestOptions,
    ) => Promise<PhotovoltaicReadings>
}

const PHOTOVOLTAIC_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/Fotovoltaico`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/api/v1/Fotovoltaico`
const PHOTOVOLTAIC_TAG = 'photovoltaic'

const photovoltaicCRUD: PhotovoltaicCrudType = () => ({
    getOne: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}`)
        const fotovoltaicoDTO = await makeRequest<NuovoFotovoltaicoDTO>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        const plant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return plant
    },
    getOneByFacilityId: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}`)
        const fotovoltaicoDTO = await makeRequest<NuovoFotovoltaicoDTO>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        const plant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return plant
    },
    getMany: async (itemsPerPage, params, onlyEnabled = false, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage)
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

        if (onlyEnabled) url.searchParams.append('OnlyActive', 'true')

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        if (params?.name) url.searchParams.append('Filters[nome]', params.name)
        if (params?.active) url.searchParams.append('Filters[attivo]', params.active)
        if (params?.typeName) url.searchParams.append('Filters[nomeTipologia]', params.typeName)
        if (params?.typeId) url.searchParams.append('Filters[idTipologieFotovoltaico]', params.typeId)
        if (params?.facilityId) url.searchParams.append('Filters[idImpianto]', params.facilityId)
        if (params?.facilityName) url.searchParams.append('Filters[nomeImpianto]', params.facilityName)

        const fotovoltaicoPaginationDTO = await makeRequest<FotovoltaicoPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        const plants = mapToPhotovoltaicPlants(fotovoltaicoPaginationDTO)
        return plants
    },
    getAll: async (options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/Pagination`)
        url.searchParams.append('PageSize', '999999')
        const fotovoltaicoPaginationDTO = await makeRequest<FotovoltaicoPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        const plantsWithPagination = mapToPhotovoltaicPlants(fotovoltaicoPaginationDTO)
        return plantsWithPagination.plants ?? []
    },
    getAllModels: async (options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/configuration`)
        const tipologieFotovoltaicoDTO = await makeRequest<TipologieFotovoltaicoDTO[]>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        const allModels = tipologieFotovoltaicoDTO.map((element) => mapToPhotovoltaicModel(element))
        return allModels
    },
    post: async (plant, options) => {
        const nuovoFotovoltaicoDTO = mapToFotovoltaicoDTO(plant)
        const url = new URL(PHOTOVOLTAIC_URL)
        const fotovoltaicoDTO = await makeRequest<NuovoFotovoltaicoDTO>(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nuovoFotovoltaicoDTO),
            ...options,
        })
        const newPhotovoltaicPlant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return newPhotovoltaicPlant
    },
    put: async (plant, options) => {
        const fotovoltaicoDTO = mapToFotovoltaicoDTO(plant)
        const url = new URL(PHOTOVOLTAIC_URL)
        const responseFotovoltaicoDTO = await makeRequest<NuovoFotovoltaicoDTO>(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fotovoltaicoDTO),
            ...options,
        })
        const responsePhotovoltaicPlant = mapToPhotovoltaicPlant(responseFotovoltaicoDTO) as PhotovoltaicPlant
        return responsePhotovoltaicPlant
    },
    patchFacility: async (id, facilityId, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}/impianto/${facilityId.toString()}`)
        const responseFotovoltaicoDTO = await makeRequest<NuovoFotovoltaicoDTO>(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            ...options,
        })
        const responsePhotovoltaicPlant = mapToPhotovoltaicPlant(responseFotovoltaicoDTO) as PhotovoltaicPlant
        return responsePhotovoltaicPlant
    },
    delete: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}`)
        await makeRequest(url, {
            method: 'DELETE',
            ...options,
        })
    },
    getProduction: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/production`)
        const { from, to, strategy, onlyLastValue } = options || {}
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await makeRequest<DatiMisureFotovoltaicoDTO | null>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })
        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const production = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return production
    },
    getConsumption: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/consumption`)
        const { from, to, strategy, onlyLastValue } = options || {}
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await makeRequest<DatiMisureFotovoltaicoDTO | null>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })

        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const consuption = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return consuption
    },
    getCo2: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/co2`)
        const { from, to, strategy, onlyLastValue } = options || {}
        if (from) url.searchParams.append('timestampFrom', from)
        if (to) url.searchParams.append('timestampTo', to)
        if (strategy) url.searchParams.append('aggregationStrategy', strategy.toString())
        if (onlyLastValue) url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await makeRequest<DatiMisureFotovoltaicoDTO | null>(url, {
            method: 'GET',
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...options,
        })

        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const co2 = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return co2
    },
})

export {
    PHOTOVOLTAIC_TAG,
    photovoltaicCRUD,
    type PhotovoltaicCrudType,
    type PhotovoltaicParams as PhotovoltaicGetOptions,
}
